import { FrameContexts } from "@microsoft/teams-js";
import { clsx } from "clsx";
import { DateTime } from "luxon";
import React, { type RefObject, useMemo } from "react";
import { useSelector } from "react-redux";
import type { ConfigState } from "../../../state/config/config.reducer";
import type { AppState } from "../../../state/root.reducer";
import type {
  MyWorkspaceMetadata,
  Project,
} from "../../../state/workspaces/types";
import { createSelectDashboardWorkspaceDetailsById } from "../../../state/workspaces/workspaces.selector";
import { useI18n } from "../../../utils/hooks/use.i18n";
import { hiconToHoyluUiIconsCentered } from "../../../utils/icon.map";
import Styles from "./WorkspaceCardThumbnail.module.css";

type WorkspaceCardThumbnailProps = {
  config: ConfigState;
  isLimited: boolean;
  isOptionsMenuOpen: boolean;
  myWorkspaceMetadata: MyWorkspaceMetadata;
  openMoreOptions: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  outsideElementRef: RefObject<HTMLDivElement>;
  project: Project | undefined;
  showWorkspaceModifiedNotification: boolean;
};

const WorkspaceCardThumbnail = ({
  config,
  isLimited,
  isOptionsMenuOpen,
  myWorkspaceMetadata,
  openMoreOptions,
  outsideElementRef,
  project,
  showWorkspaceModifiedNotification,
}: WorkspaceCardThumbnailProps) => {
  const tWorkspaceCard = useI18n("WORKSPACE_CARD.");
  const tFilterByName = useI18n("FILTER_BY_NAME.");

  const selectDashboardWorkspaceDetailsById = useMemo(
    createSelectDashboardWorkspaceDetailsById,
    [],
  );

  const selectedDashboardWorkspaceDetails = useSelector((state: AppState) =>
    selectDashboardWorkspaceDetailsById(state, myWorkspaceMetadata.workspaceId),
  );

  const isTemplate = !!myWorkspaceMetadata.templateId;
  const isWorkspaceHidden = myWorkspaceMetadata.isHidden;
  const isWorkspaceLive = selectedDashboardWorkspaceDetails?.isLive;

  // WorkspaceCard title and footer styles
  const [
    displayedWorkspaceName,
    workspaceThumbnailFooterBg,
    workspaceSubtitle,
    color,
  ] = useMemo(() => {
    const workspaceName =
      myWorkspaceMetadata.workspaceName || tWorkspaceCard("UNTITLED");
    const lastAccessed = myWorkspaceMetadata.lastAccess
      ? DateTime.fromISO(myWorkspaceMetadata.lastAccess, {
          zone: "utc",
        }).toLocaleString(DateTime.DATE_MED)
      : tWorkspaceCard("NOT_YET");

    // ws under credit limit
    let name = workspaceName;
    let backGround = Styles.defaultThumbnail;
    let subtitle = lastAccessed;
    let color = "";

    if (project?.status === "Inactive") {
      // rules only for ws assigned to the inactive project
      name = `${tFilterByName("INACTIVE")}: ${workspaceName}`;
      backGround = Styles.inactiveThumbnail;
    } else if (isLimited) {
      // rules only for ws (exclude project ws) when tonnage is exceeded
      name = `${tWorkspaceCard("LIMITED_TITLE").toUpperCase()}: ${workspaceName}`;
      subtitle = isWorkspaceLive ? tWorkspaceCard("LOADED") : subtitle;
      backGround = Styles.limitedThumbnail;
    } else if (isWorkspaceHidden) {
      subtitle = tWorkspaceCard("HIDDEN_WORKSPACE");
      color = isWorkspaceLive ? Styles.liveWorkspaceThumbnail : Styles.hidden;
    } else if (isWorkspaceLive) {
      subtitle = tWorkspaceCard("LOADED");
      backGround = Styles.liveWorkspaceThumbnail;
    }

    return [name, backGround, subtitle, color];
  }, [
    isLimited,
    isWorkspaceHidden,
    isWorkspaceLive,
    myWorkspaceMetadata.lastAccess,
    myWorkspaceMetadata.workspaceName,
    project?.status,
    tFilterByName,
    tWorkspaceCard,
  ]);

  return (
    <div
      className={clsx(
        color,
        Styles.workspaceCardThumbnail,
        isTemplate ? Styles.template : workspaceThumbnailFooterBg,
      )}
    >
      <div className={Styles.workspaceInfo}>
        <div className={Styles.workspaceInfoItems}>
          <div className={Styles.workspaceNameContainer}>
            {isWorkspaceHidden && (
              <div
                className={clsx("hoylu-ui-icons-indicator-hide", Styles.icon)}
                style={{ color }}
              />
            )}
            {project && (
              <div
                className={clsx(
                  hiconToHoyluUiIconsCentered(project.icon),
                  Styles.icon,
                )}
                style={{ color: project.color }}
              />
            )}
            <h1 className={Styles.workspaceName}>{displayedWorkspaceName}</h1>
          </div>
          <div className={Styles.subtitleContainer}>
            {isTemplate ? (
              <span>{tWorkspaceCard("TEMPLATE")}</span>
            ) : (
              <div className={Styles.workspaceSubtitleContainer}>
                <p
                  className={Styles.workspaceSubtitle}
                  title={workspaceSubtitle}
                >
                  {workspaceSubtitle}
                </p>
                {showWorkspaceModifiedNotification && (
                  <div className={Styles.workspaceModifiedNotification} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={Styles.meatballsMenuContainer}>
        <div ref={outsideElementRef}>
          <button
            aria-label={myWorkspaceMetadata.workspaceName ?? undefined}
            className={Styles.meatballsMenu}
            data-test-id="workspace-options"
            disabled={config.teamsFrame === FrameContexts.sidePanel}
            onClick={openMoreOptions}
            title={tWorkspaceCard("MORE_OPTIONS")}
            type="button"
          >
            <svg
              aria-hidden={true}
              fill={isOptionsMenuOpen ? "#AECBFA" : "#716C83"}
              focusable={false}
              height="6px"
              viewBox="0 0 22 6"
              width="18px"
            >
              <path
                d="M5.24,4.248 C5.24,4.952 4.664,5.496 3.992,5.496 L1.464,5.496 C0.76,5.496 0.216,4.92 0.216,4.248 L0.216,1.72 C0.216,1.016 0.792,0.472 1.464,0.472 L3.96,0.472 C4.664,0.472 5.208,1.048 5.208,1.72 L5.208,4.248 L5.24,4.248 Z"
                id="Path"
              />
              <path
                d="M13.528,4.248 C13.528,4.952 12.952,5.496 12.28,5.496 L9.752,5.496 C9.048,5.496 8.504,4.92 8.504,4.248 L8.504,1.72 C8.504,1.016 9.08,0.472 9.752,0.472 L12.28,0.472 C12.984,0.472 13.528,1.048 13.528,1.72 L13.528,4.248 Z"
                id="Path"
              />
              <path
                d="M21.816,4.248 C21.816,4.952 21.24,5.496 20.568,5.496 L18.04,5.496 C17.336,5.496 16.792,4.92 16.792,4.248 L16.792,1.72 C16.792,1.016 17.368,0.472 18.04,0.472 L20.568,0.472 C21.272,0.472 21.816,1.048 21.816,1.72 L21.816,4.248 Z"
                id="Path"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceCardThumbnail;
